import { api } from './api';

/**
 * Configuração do WIRESEO — ADMIN-ONLY (plano §10 e §11.2).
 *
 * Contratos conferidos contra o backend real (`app/api/v1/endpoints/wireseo.py`):
 *
 *   GET  /wireseo/params          → { rows, defaults, efetivo }
 *   PUT  /wireseo/params          ← { scope, scope_ref, valores: {chave: valor} }
 *   GET  /wireseo/registry        → { rows, efetivo }
 *   PUT  /wireseo/registry        ← { kind, slug, rotulo?, instrucao?, habilitado?, exige_liberacao_chefe? }
 *   GET  /wireseo/fontes-oficiais → { rows, allowlist }
 *   PUT  /wireseo/fontes-oficiais ← { orgao, dominio, ativo }
 *
 * Nenhuma destas rotas aceita `client`: o dono da linha é o principal do token,
 * NUNCA o corpo do pedido. Por isso o seletor de cliente do topo da tela não
 * vale aqui — quem é staff grava a linha padrão da casa, e a tela diz isso em
 * vez de fingir que está editando o cliente escolhido no seletor.
 *
 * Em `valores`, `null` APAGA a chave (é assim que se volta ao default de
 * fábrica sem gravar um valor competindo com ele).
 */

export type ParamScope = 'plataforma' | 'esteira' | 'editoria' | 'materia';

export const SCOPES: { key: ParamScope; rotulo: string; explicacao: string }[] = [
  {
    key: 'plataforma',
    rotulo: 'Plataforma',
    explicacao: 'Camada do engenheiro: modelos, janelas numéricas, tetos, allowlist.',
  },
  {
    key: 'esteira',
    rotulo: 'Esteira',
    explicacao: 'Tom e tipos padrão da esteira, objetivo, e o losango liga/percentual.',
  },
  {
    key: 'editoria',
    rotulo: 'Editoria / chefe',
    explicacao: 'Tons e tipos permitidos, intensidade máxima, palavras proibidas.',
  },
  {
    key: 'materia',
    rotulo: 'Matéria',
    explicacao: 'Escolha do jornalista na matéria: tom, tipo, objetivo, instrução livre.',
  },
];

export interface ParamRow {
  id: string;
  client_id: string | null;
  scope: ParamScope;
  scope_ref: string;
  chave: string;
  valor: unknown;
  updated_by: string | null;
  updated_at: string | null;
}

export interface ParamsResposta {
  rows: ParamRow[];
  /** default de fábrica de cada chave — o que vale sem nenhuma linha gravada */
  defaults: Record<string, unknown>;
  /** o que o motor vai realmente usar, já resolvida a cascata */
  efetivo: Record<string, unknown>;
}

export type RegistryKind = 'tipo' | 'tom';

export interface RegistryRow {
  id: string;
  client_id: string | null;
  kind: RegistryKind;
  slug: string;
  rotulo: string;
  /** texto injetado no prompt — segredo industrial, só sai para admin */
  instrucao: string;
  habilitado: boolean;
  exige_liberacao_chefe: boolean;
}

export interface RegistryResposta {
  rows: RegistryRow[];
  efetivo: {
    tipos_habilitados?: string[];
    tipos_permitidos?: string[];
    tons_habilitados?: string[];
    tons_permitidos?: string[];
    tipos_exigem_liberacao?: string[];
    tons_exigem_liberacao?: string[];
  } & Record<string, unknown>;
}

export interface FonteRow {
  id: string;
  client_id: string | null;
  orgao: string;
  dominio: string;
  ativo: boolean;
}

export interface FontesResposta {
  rows: FonteRow[];
  /**
   * Órgão para UM domínio — o eleito do degrau 1 da F1 (`site:dominio termo`).
   * É `str → str`, não `str → list`: quando um órgão tem dois domínios, o
   * backend elege um de forma determinística.
   */
  fontes: Record<string, string>;
  /**
   * TODOS os domínios ATIVOS, em lista plana — a allowlist do degrau 4 da F1.
   * É por aqui que se pergunta "este domínio está liberado?", e não pelo mapa
   * `fontes`: um órgão pode ter um segundo domínio legítimo, que existe na
   * allowlist sem ser o eleito.
   *
   * ATENÇÃO ao formato: já foi `Record<string, string[]>` numa versão anterior
   * do backend. Indexar esta LISTA pelo nome do órgão devolve `undefined` em
   * silêncio, e foi exatamente assim que a coluna "na allowlist" nasceu
   * respondendo "não" para as 15 fontes ativas.
   */
  allowlist: string[];
}

export const ENDPOINTS = {
  params: '/wireseo/params',
  registry: '/wireseo/registry',
  fontes: '/wireseo/fontes-oficiais',
} as const;

export const wireseoConfigApi = {
  getParams: (): Promise<ParamsResposta> => api.get<ParamsResposta>(ENDPOINTS.params),

  /** Grava (ou apaga, com `null`) chaves de UMA camada. */
  putParams: (patch: {
    scope: ParamScope;
    scope_ref?: string;
    valores: Record<string, unknown>;
  }): Promise<{ gravadas: number; apagadas: number; scope: string }> =>
    api.put(ENDPOINTS.params, {
      scope: patch.scope,
      scope_ref: patch.scope_ref ?? '',
      valores: patch.valores,
    }),

  getRegistry: (): Promise<RegistryResposta> => api.get<RegistryResposta>(ENDPOINTS.registry),

  putRegistry: (patch: {
    kind: RegistryKind;
    slug: string;
    rotulo?: string;
    instrucao?: string;
    habilitado?: boolean;
    exige_liberacao_chefe?: boolean;
  }): Promise<{ kind: string; slug: string; ok: boolean }> => api.put(ENDPOINTS.registry, patch),

  getFontes: (): Promise<FontesResposta> => api.get<FontesResposta>(ENDPOINTS.fontes),

  putFonte: (patch: {
    orgao: string;
    dominio: string;
    ativo: boolean;
  }): Promise<{ orgao: string; dominio: string; ativo: boolean }> =>
    api.put(ENDPOINTS.fontes, patch),
};

/** Chaves do losango, por esteira (§12.1). */
export const CHAVE_ATIVO = 'wireseo_ativo';
export const CHAVE_PERCENTUAL = 'wireseo_percentual';
